import { useLazyQuery, useQuery, useMutation } from '@apollo/client';
import {
  ALL_MODULES,
  AppContext,
  B1,
  EventEmitter,
  getTranslatedText,
  Icon,
  SelectionSheet,
  toast,
  BackgroundIcon,
  Loader,
  NoDataCard,
  getParsedCrudData,
  useActivityTracker,
  PSA_CONSTANTS,
  EventEmitterWithIndex
} from '@nile/components';
import EmptyContainer from '@nile/components/src/Containers/EmptyContainer';
import {
  DELETE_TASK,
  GET_ALL_TEMPLATELIST,
  GET_TASK_LIST,
  TicketClient,
  UPDATE_TASK
} from '@nile/graphql';
import { GET_SOP } from '@nile/graphql/src/Query/sop';
import envConfig from '@nile/home/src/Config/env';
import { TaskCard } from '@nile/projects/src/Components/Cards';
import { constructTaskObject } from '@nile/projects/src/Utils';
import { Sheet, Button, Avatar } from '@superops/ui-design-system';
import { PlusCircle } from '@styled-icons/fa-solid/PlusCircle';
import { Timer } from '@styled-icons/remix-fill';
import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState
} from 'react';
import { FormattedMessage, injectIntl } from 'react-intl';
import styled from 'styled-components';
import { SVGIcon } from '@superops/super-icons';
import { SuperSortable } from '@nile/components/src/SuperSortable/SuperSortable';
import { pick, sortBy } from 'lodash';
import { UPDATE_TASKS } from '@nile/graphql/src/Mutation/task';
import AssociateTasks from '@nile/mangifera/src/Containers/ChatContainers/AssociateTasks';

const Wrapper = styled.div`
  background-image: url(${envConfig.imageRoot}svg/chatBg.svg);
  background-size: 360px;
  background-color: var(--sops-ticket-coversation-bg);
  overflow: auto;
  padding: 12px;
`;

const TaskCardWrapper = styled.div`
  margin-top: 12px;
`;

function SOPTasks(props) {}

function AssociatedTasks(props) {
  const {
    data,
    updateTaskList,
    ticketId,
    readOnly,
    onTaskChange,
    onDeleteSuccess
  } = props;

  const handleTaskUpdation = (id, task) => {
    const taskObj = constructTaskObject(task);
    TicketClient.mutate({
      mutation: UPDATE_TASK,
      variables: { taskId: id, module: ALL_MODULES.ticket, input: taskObj }
    }).then((res) => {
      if (res?.data?.updateTask) {
        props.updateTaskList();
        props.refetchTicket();
        toast('success', 'Task updated successfully');
      } else {
        // toast('error', 'Something went wrong');
      }
    });
  };

  const handleTaskDeletion = (id) => {
    TicketClient.mutate({
      mutation: DELETE_TASK,
      variables: { taskId: id, module: ALL_MODULES.ticket }
    }).then((res) => {
      if (res?.data?.deleteTask) {
        props.updateTaskList().then(onDeleteSuccess);
        props.refetchTicket();
        toast('success', 'ticket.details.tasks.taskDelete.success');
        EventEmitterWithIndex.dispatch('refetchTimerEntries');
      } else {
        // toast('error', 'Something went wrong');
      }
    });
  };

  const [loadingUpdateTasksList, setLoadingUpdateTasksList] = useState(false);

  return (
    <div className="full-width">
      {data?.length === 0 ? (
        <div className="full-width flex center jcenter mt-30">
          <NoDataCard
            imageURL={`url(${envConfig.imageRoot}svg/task-empty-state.svg)`}
            text={<FormattedMessage id="ticket.details.tasks.emptyState" />}
            description={
              <FormattedMessage id="ticket.details.tasks.emptyState.description" />
            }
            type="secondary"
          />
        </div>
      ) : (
        ''
      )}
      <SuperSortable
        onDragEnd={(modifiedList) => {
          // eslint-disable-next-line react-hooks/rules-of-hooks
          useActivityTracker({
            event: PSA_CONSTANTS.MIX_PANEL_EVENTS_FOR_REORDER.TICKET
          });
          onTaskChange({
            list: modifiedList.map((task, index) => ({
              ...task,
              taskOrder: index + 1
            })),
            startLoader: () => setLoadingUpdateTasksList(true),
            endLoader: () => setLoadingUpdateTasksList(false)
          });
        }}
        list={data}
      >
        {data.map((el, idx) => {
          return (
            <TaskCardWrapper
              className={loadingUpdateTasksList && 'disable-actions'}
            >
              <TaskCard
                task={el}
                index={el.taskId}
                handleTaskDeletion={handleTaskDeletion}
                handleTaskUpdation={handleTaskUpdation}
                isTicketDetailsPage
                module="TICKET_TASK"
                details={{
                  operation: 'UPDATE',
                  ticketId: ticketId,
                  taskId: el.taskId
                }}
                disableActions={readOnly}
                disableView={readOnly}
                enableDrag={!readOnly}
              />
            </TaskCardWrapper>
          );
        })}
      </SuperSortable>
    </div>
  );
}

function TicketTask(props) {
  const { intl, readOnly } = props;
  const { sop, ticketId } = props.ticket;
  const [sheetActive, setSheetActive] = useState(false);
  const [tasks, setTasks] = useState([]);
  const toggleSheetActive = useCallback(
    () => setSheetActive((active) => !active),
    []
  );
  const context = useContext(AppContext);

  const baseTasksListRef = useRef([]);

  const { loading, error, data, refetch } = useQuery(GET_TASK_LIST, {
    fetchPolicy: 'network-only',
    variables: {
      listInfo: {
        condition: {
          joinOperator: 'AND',
          operands: [
            {
              attribute: 'ticket.ticketId',
              operator: 'is',
              value: ticketId
            }
          ]
        },
        sort: [
          {
            attribute: 'taskOrder',
            order: 'ASC',
            emptyAtLast: true
          }
        ]
      },
      module: 'TICKET'
    },
    onCompleted({ getTasks }) {
      setTasks(getTasks?.tasks || []);
      baseTasksListRef.current = getTasks?.tasks || [];
    }
  });
  const updateTasks = () => {
    return refetch().then((res) => {
      if (res?.data?.getTasks?.tasks) {
        setTasks(res?.data?.getTasks?.tasks);
        baseTasksListRef.current = res.data.getTasks.tasks;
        return res.data.getTasks.tasks;
      }
    });
  };
  useEffect(() => {
    EventEmitter.subscribe('taskAddition', updateTasks);
    return function cleanup() {
      EventEmitter.unsubscribe('taskAddition');
    };
  }, []);
  const updateTaskList = useCallback((taskList) => {
    props.createTasks(taskList, updateTasks);
  });

  const [updateTasksList] = useMutation(UPDATE_TASKS);

  if (loading)
    return (
      <div className="fx actr jcenter w100 h-300">
        <Loader />
      </div>
    );

  async function onTaskReorder({
    list: reorderedList,
    startLoader,
    endLoader
  }) {
    setTasks(reorderedList);

    const { updatedItems } = getParsedCrudData(
      baseTasksListRef.current,
      reorderedList,
      'taskId'
    );

    if (!updatedItems.length) return;

    startLoader();

    updateTasksList({
      variables: {
        input: {
          workItem: {
            workId: ticketId,
            module: 'TICKET'
          },
          tasks: updatedItems.map((task) => pick(task, ['taskOrder', 'taskId']))
        }
      }
    }).finally(endLoader);
  }

  function onTaskDelete(respTasks) {
    if (!respTasks) return;

    const newList = respTasks.map((t, i) => ({
      ...t,
      taskOrder: i + 1
    }));

    const { updatedItems } = getParsedCrudData(
      baseTasksListRef.current,
      newList,
      'taskId'
    );

    updateTasksList({
      variables: {
        input: {
          workItem: {
            workId: ticketId,
            module: 'TICKET'
          },
          tasks: updatedItems.map((task) => pick(task, ['taskOrder', 'taskId']))
        }
      }
    });

    EventEmitter.dispatch('refetchWorklogs');
  }

  return (
    <Wrapper className="full-height">
      {/* {sop && <SOPTasks/>} */}
      <div className="full-width">
        <div className="flex center">
          <B1 className="ml8">{`${getTranslatedText(
            intl,
            'ticket.details.tasks.header'
          )} (${tasks?.length || 0})`}</B1>
          {!readOnly && (
            <div className="mlauto associateTasksBtn" id="associateTasksHolder">
              <Button
                id="copy"
                secondary
                onClick={toggleSheetActive}
                icon={<SVGIcon name="Add" color="#323232" />}
              >
                <FormattedMessage id="ticket.details.tasks.create" />
              </Button>
            </div>
          )}
        </div>
        {!readOnly && (
          <Sheet open={sheetActive} onClose={toggleSheetActive}>
            <AssociateTasks
              ticket={props.ticket}
              intl={intl}
              toggleSheetActive={toggleSheetActive}
              updateTaskList={updateTaskList}
              context={context}
            />
          </Sheet>
        )}
        <AssociatedTasks
          data={tasks}
          intl={intl}
          updateTaskList={updateTasks}
          refetchTicket={props.refetchTicket}
          ticketId={ticketId}
          readOnly={readOnly}
          onTaskChange={onTaskReorder}
          onDeleteSuccess={onTaskDelete}
        />
      </div>
    </Wrapper>
  );
}

export default injectIntl(TicketTask);
